/**
 * Draco Exporter Utility for Three.js
 * Exports 3D models to JSON with embedded .drc data
 *
 * @example
 * // Single mesh
 * await downloadAsJSON(mesh);
 *
 * // Array of meshes
 * await downloadAsJSON([tooth1, tooth2, tooth3]);
 */

import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

// Export target configuration
export const ExportTarget = Object.freeze({
  Default: "default",
  Unity: "unity",
});

let currentExportTarget = ExportTarget.Default;

export function setExportTarget(target) {
  currentExportTarget = target;
}

export function getExportTarget() {
  return currentExportTarget;
}

// Handedness conversion: Three.js (right-handed Y-up) ↔ Unity (left-handed Y-up)
const HANDEDNESS_MATRIX = new THREE.Matrix4().makeScale(1, 1, -1);

/**
 * Convert a Three.js Matrix4 for the current export target.
 * For Unity: applies C * M * C where C = scale(1,1,-1) to flip handedness.
 * For Default: returns the matrix array as-is.
 *
 * @param {THREE.Matrix4} matrix - The Three.js Matrix4 to convert
 * @returns {number[]} - The 16-element column-major flat matrix array
 */
export function convertMatrixForExportTarget(matrix) {
  if (currentExportTarget !== ExportTarget.Unity) {
    return matrix.toArray();
  }
  const converted = HANDEDNESS_MATRIX.clone()
    .multiply(matrix)
    .multiply(HANDEDNESS_MATRIX);
  return converted.toArray();
}

/**
 * Convert a [x, y, z] array for the current export target.
 * For Unity: negates Z. For Default: returns as-is.
 *
 * @param {number[]} arr - [x, y, z]
 * @returns {number[]} - converted [x, y, z]
 */
export function convertVector3ForExportTarget(arr) {
  if (currentExportTarget !== ExportTarget.Unity) {
    return arr;
  }
  return [arr[0], arr[1], -arr[2]];
}

let encoderModule = null;
let encoderPending = null;
let encoderPath = `${process.env.PUBLIC_URL}/Draco/`;

/**
 * Set the path to the Draco encoder files
 */
export function setEncoderPath(path) {
  encoderPath = path.endsWith("/") ? path : path + "/";
}

/**
 * Initialize the Draco encoder module
 */
async function initDracoEncoder() {
  if (encoderModule) return encoderModule;
  if (encoderPending) return encoderPending;

  encoderPending = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `${encoderPath}draco_encoder.js`;

    script.onload = () => {
      if (typeof DracoEncoderModule === "undefined") {
        reject(new Error("DracoEncoderModule not found"));
        return;
      }

      DracoEncoderModule({
        locateFile: filename => `${encoderPath}${filename}`,
      })
        .then(module => {
          encoderModule = module;
          resolve(module);
        })
        .catch(reject);
    };

    script.onerror = () => reject(new Error("Failed to load draco_encoder.js"));
    document.head.appendChild(script);
  });

  return encoderPending;
}

/**
 * Convert Uint8Array to Base64 string
 */
function uint8ArrayToBase64(uint8Array) {
  let binary = "";
  const len = uint8Array.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(uint8Array[i]);
  }
  return btoa(binary);
}

/**
 * Export a single Three.js object to Draco format
 *
 * @param {THREE.Object3D} object - Mesh, Group, or any Object3D
 * @param {Object} [options] - Compression options
 * @param {number} [options.positionBits=14] - Position quantization bits (10-14)
 * @param {boolean} [options.includeChildren=false] - Include child meshes
 * @param {number} [options.encodeSpeed=1] - Encoding speed (0-10, higher = faster but larger)
 * @param {number} [options.decodeSpeed=1] - Decoding speed (0-10, higher = faster decoding)
 * @param {boolean} [options.centerGeometry=false] - Center geometry at origin
 * @param {boolean} [options.applyTransforms=false] - Bake object's matrixWorld into geometry (export in world space)
 * @param {THREE.BufferGeometry} [options._overrideGeometry] - Use this geometry instead of extracting from object
 * @returns {Promise<string>} - Draco binary data as base64 string
 */
export async function exportToDraco(object, options = {}) {
  let {
    positionBits = 14,
    includeChildren = false,
    encodeSpeed = 1,
    decodeSpeed = 1,
    centerGeometry = false,
    applyTransforms = false,
    _overrideGeometry = null,
  } = options;

  const encoder = await initDracoEncoder();

  let geometry;

  // Use override geometry if provided
  if (_overrideGeometry) {
    geometry = _overrideGeometry.clone();
  } else {
    // Collect meshes
    const meshes = [];

    if (includeChildren) {
      object.traverse(child => {
        if (child.isMesh && child.geometry) meshes.push(child);
      });
    } else {
      if (object.isMesh && object.geometry) meshes.push(object);
    }

    if (meshes.length === 0)
      throw new Error(`No meshes found in object: ${object.name || "unnamed"}`);

    // Clone and process geometries
    if (meshes.length === 1) {
      geometry = meshes[0].geometry.clone();

      // Bake world transform into geometry vertices
      if (applyTransforms) {
        meshes[0].updateWorldMatrix(true, false);
        geometry.applyMatrix4(meshes[0].matrixWorld);
      }
      if (centerGeometry) {
        geometry.center();
      }
    } else {
      const geos = meshes.map(mesh => {
        const geo = mesh.geometry.clone();

        // Bake world transform into geometry vertices
        if (applyTransforms) {
          mesh.updateWorldMatrix(true, false);
          geo.applyMatrix4(mesh.matrixWorld);
        }
        if (centerGeometry) {
          geo.center();
        }
        return geo;
      });
      geometry = mergeGeometries(geos, false);
    }
  }

  // Ensure geometry is indexed
  if (!geometry.index) {
    const posCount = geometry?.getAttribute("position")?.count;
    const indices = [];
    for (let i = 0; i < posCount; i++) indices.push(i);
    geometry.setIndex(indices);
  }

  // Apply coordinate system conversion for export target
  if (currentExportTarget === ExportTarget.Unity) {
    // Flip Z axis for left-handed coordinate system (positions + normals)
    geometry.applyMatrix4(HANDEDNESS_MATRIX);
    // Reverse triangle winding order (handedness flip inverts face orientation)
    const idxArray = geometry.index.array;
    for (let i = 0; i < idxArray.length; i += 3) {
      const tmp = idxArray[i + 1];
      idxArray[i + 1] = idxArray[i + 2];
      idxArray[i + 2] = tmp;
    }
  }

  // Get geometry data
  const positions = geometry.getAttribute("position");
  const normals = geometry.getAttribute("normal");
  const indices = geometry.index;

  const numPoints = positions.count;
  const numFaces = indices.count / 3;

  // Create encoder and mesh
  const dracoEncoder = new encoder.Encoder();
  const meshBuilder = new encoder.MeshBuilder();
  const dracoMesh = new encoder.Mesh();

  // Add faces
  const faces = new Uint32Array(indices.array);
  meshBuilder.AddFacesToMesh(dracoMesh, numFaces, faces);

  // Add position attribute
  meshBuilder.AddFloatAttributeToMesh(
    dracoMesh,
    encoder.POSITION,
    numPoints,
    3,
    new Float32Array(positions.array),
  );

  // Add normals if present
  if (normals) {
    meshBuilder.AddFloatAttributeToMesh(
      dracoMesh,
      encoder.NORMAL,
      numPoints,
      3,
      new Float32Array(normals.array),
    );
  }

  // Configure encoder
  dracoEncoder.SetSpeedOptions(encodeSpeed, decodeSpeed);
  dracoEncoder.SetAttributeQuantization(encoder.POSITION, positionBits);
  if (normals) {
    dracoEncoder.SetAttributeQuantization(encoder.NORMAL, 10);
  }
  dracoEncoder.SetEncodingMethod(encoder.MESH_EDGEBREAKER_ENCODING);

  // Encode
  const encodedData = new encoder.DracoInt8Array();
  const encodedLen = dracoEncoder.EncodeMeshToDracoBuffer(
    dracoMesh,
    encodedData,
  );

  if (encodedLen <= 0) {
    encoder.destroy(encodedData);
    encoder.destroy(dracoMesh);
    encoder.destroy(meshBuilder);
    encoder.destroy(dracoEncoder);
    geometry.dispose();
    throw new Error("Draco encoding failed");
  }

  // Copy result and convert to base64
  const result = new Uint8Array(encodedLen);
  for (let i = 0; i < encodedLen; i++) {
    result[i] = encodedData.GetValue(i);
  }
  const base64Result = uint8ArrayToBase64(result);

  // Cleanup
  encoder.destroy(encodedData);
  encoder.destroy(dracoMesh);
  encoder.destroy(meshBuilder);
  encoder.destroy(dracoEncoder);
  geometry.dispose();

  return base64Result;
}

/**
 * Export to Draco with a custom transform matrix applied to the geometry.
 * The matrix is applied to the cloned geometry before encoding.
 *
 * @param {THREE.Object3D} object - Mesh or Group to export
 * @param {THREE.Matrix4} [transformMatrix] - Matrix to apply to the geometry (e.g. matrixWorld, centering offset)
 * @param {Object} [options] - Compression options (same as exportToDraco)
 * @returns {Promise<{data: string}>} - data is base64 encoded draco string
 */
export async function exportToDracoWithTransform(
  object,
  transformMatrix,
  options = {},
) {
  const geometry = object.geometry.clone();
  if (transformMatrix) {
    geometry.applyMatrix4(transformMatrix);
  }

  const dracoData = await exportToDraco(object, {
    ...options,
    _overrideGeometry: geometry,
  });

  return dracoData;
}

/**
 * Export to Draco in world space — bakes the full matrixWorld into the geometry
 * so the exported mesh is positioned globally, regardless of parent hierarchy.
 *
 * @param {THREE.Object3D} object - Mesh to export
 * @param {THREE.Matrix4} [centeringOffset] - Optional centering offset matrix to also apply
 *   (e.g. to undo a scene-level offset). Applied as: centeringOffset^-1 * matrixWorld
 * @param {Object} [options] - Compression options (same as exportToDraco)
 * @returns {Promise<{data: string}>} - data is base64 encoded draco string
 */
export async function exportToDracoInWorldSpace(
  object,
  centeringOffset,
  options = {},
) {
  if (!isValidJaw(object)) {
    return {};
  }
  object.updateWorldMatrix(true, false);
  const geometry = object.geometry.clone();

  if (centeringOffset) {
    // Undo centering then apply world: centeringOffset^-1 * matrixWorld
    const combined = centeringOffset
      .clone()
      .invert()
      .multiply(object.matrixWorld);
    geometry.applyMatrix4(combined);
  } else {
    geometry.applyMatrix4(object.matrixWorld);
  }

  const dracoData = await exportToDraco(object, {
    ...options,
    _overrideGeometry: geometry,
  });

  return {
    data: dracoData,
  };
}

function isValidJaw(jawObject) {
  if (!jawObject || !jawObject?.geometry?.attributes?.position) {
    return false;
  }

  return true;
}

/**
 * Export multiple objects, each with their own transform
 * Useful for scenes where you want individual .drc files
 */
export async function exportSceneToDraco(objects, options = {}) {
  const results = [];

  for (const obj of objects) {
    obj.updateWorldMatrix(true, false);

    results.push({
      name: obj.name,
      transform: obj.matrixWorld.toArray(),
      draco: await exportToDraco(obj, {
        ...options,
        applyTransforms: false,
        centerGeometry: false,
      }),
    });
  }

  return results;
}

/**
 * Export single object to JSON with embedded .drc data
 *
 * @param {THREE.Object3D} object - Mesh or Group
 * @param {Object} [options] - Export options
 * @returns {Promise<Object>} - { name, data } where data is base64 encoded .drc
 */
export async function exportToJSON(object, options = {}) {
  const drcData = await exportToDraco(object, options);

  return {
    name: object.name || "unnamed",
    data: drcData, // Already base64
  };
}

/**
 * Export array of objects to JSON
 *
 * @param {THREE.Object3D|THREE.Object3D[]} objects - Single object or array
 * @param {Object} [options] - Export options
 * @returns {Promise<Object[]>} - Array of { name, data }
 */
export async function exportAllToJSON(objects, options = {}) {
  const objectArray = Array.isArray(objects) ? objects : [objects];
  const results = [];

  for (const obj of objectArray) {
    try {
      const json = await exportToJSON(obj, options);
      results.push(json);
    } catch (err) {
      console.error(`Failed to export ${obj.name || "unnamed"}:`, err);
    }
  }

  return results;
}

/**
 * Download objects as a single JSON file
 *
 * @param {THREE.Object3D|THREE.Object3D[]} objects - Single object or array
 * @param {string} [filename="models"] - Output filename (without extension)
 * @param {Object} [options] - Export options
 */
export async function downloadAsJSON(
  objects,
  filename = "models",
  options = {},
) {
  const objectArray = Array.isArray(objects) ? objects : [objects];

  console.log(`Exporting ${objectArray.length} object(s)...`);

  const results = await exportAllToJSON(objectArray, options);

  const output = {
    version: 1,
    count: results.length,
    models: results,
  };

  const jsonString = JSON.stringify(output, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.json`;
  a.click();

  URL.revokeObjectURL(url);

  console.log(`Downloaded ${results.length} model(s) as ${filename}.json`);
}

/**
 * Download as .drc file (single object only)
 *
 * @param {THREE.Object3D} object - Mesh or Group
 * @param {string} [filename] - Filename (defaults to object.name)
 */
export async function downloadAsDraco(object, filename) {
  const data = await exportToDraco(object);
  const name = filename || object.name || "model";

  const blob = new Blob([data], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = `${name}.drc`;
  a.click();

  URL.revokeObjectURL(url);
}

/**
 * Preload encoder (call early to avoid delay on first export)
 */
export async function preloadEncoder() {
  await initDracoEncoder();
}
